<template>
	<v-card class="mx-auto" elevation="0">
		<v-card-title class="card-header fixed-card-title2">
			{{ $t('add-location-mapping-pcc') }}
			<v-spacer />
			<v-icon @click="closeDrawer" class="white-icon mr-3">mdi-close</v-icon>
		</v-card-title>
		<v-card-text class="pt-4">
			<v-row dense>
				<v-col cols="12">
					<v-select
						:items="mappedPccOrganizations"
						item-text="pccCompanyName"
						item-value="pccCompanyId"
						outlined
						hide-details
						dense
						:label="`${$t('pcc-organizations')}`"
						v-model="locationToAdd.pccCompanyId"
						:readonly="viewDataState !== null" />
				</v-col>

				<v-col cols="12">
					<v-select
						:items="pccLocations"
						item-text="facilityName"
						item-value="facId"
						outlined
						hide-details
						dense
						:label="`${$t('pcc-locations')}`"
						v-model="locationToAdd.pccLocationId"
						:readonly="viewDataState !== null" />
				</v-col>

				<v-col>
					<v-divider />
				</v-col>

				<v-col cols="12">
					<v-select
						class="py-1"
						hide-details
						dense
						v-model="locationToAdd.mappedOrganizationId"
						:items="allMedicalInstitutions"
						item-value="id"
						item-text="institutionName"
						:label="$t('organizations')"
						outlined
						:readonly="viewDataState !== null" />
				</v-col>

				<v-col cols="12">
					<v-select
						:items="notMappedLocationsFiltered"
						item-text="hospitalName"
						item-value="id"
						outlined
						hide-details
						dense
						:label="`${$t('location')}`"
						v-model="locationToAdd.mappedHospitalId" />
					<div v-if="foundMatch" class="px-3 v-messages error--text">{{ $t('please-check-if-match-is-correct') }}</div>
				</v-col>

				<v-col>
					<v-divider />
				</v-col>

				<v-col cols="12">
					<v-textarea
						outlined
						hide-details
						dense
						:label="`${$t('description')}`"
						v-model="locationToAdd.description"
						rows="3" />
				</v-col>

				<v-col cols="12">
					<v-checkbox
						outlined
						dense
						hide-details="auto"
						v-model="locationToAdd.finalizeMeasurementAutomatically"
						:label="`${$t('finalize-measurement-automatically')}`" />
				</v-col>

				<v-col cols="12">
					<v-checkbox
						outlined
						dense
						hide-details="auto"
						v-model="locationToAdd.residentCreationIsAutomatic"
						:label="`${$t('resident-creation-is-automatic')}`" />
				</v-col>

				<v-col cols="12">
					<v-checkbox
						outlined
						dense
						hide-details="auto"
						v-model="locationToAdd.userCreationIsAutomatic"
						:label="`${$t('user-creation-is-automatic')}`" />
				</v-col>

				<v-col cols="12" v-if="foundMatch">
					<v-btn class="save-button-style" block @click="checkLocation()">
						{{ $t('create-new-location-instead') }}
					</v-btn>
				</v-col>
			</v-row>
		</v-card-text>
		<v-container class="fixed-card-actions">
			<v-row dense>
				<v-col cols="6">
					<v-btn elevation="0" @click="closeDrawer" class="cancel-button-style capitalize-text">
						{{ $t('cancel') }}
					</v-btn>
				</v-col>
				<v-col cols="6">
					<v-btn :disabled="isSaveButtonDisabled()" @click="saveOrganizationMapping" class="save-button-style">
						{{ $t('save') }}
					</v-btn>
				</v-col>
			</v-row>
		</v-container>
	</v-card>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex';
import MappingRepository from '../../data/repositories/v1/MappingRepository';

export default {
	name: 'LocationMappingAdd',
	data: () => ({
		componentKey: 0,
		menu: false,
		defaultTimeZone: 'America/Chicago',
		selectedMedicalInstitution: null,
		ecpLocationGuid: '',
		ecpLocationName: '',
		apiKey: '',
		clientId: '',
		clientServer: '',
		notMappedLocations: [],
		notMappedLocationsFiltered: [],
		mappedPccOrganizations: [],

		locationToAdd: {
			pccLocationId: 0,
			mappedHospitalId: 0,
			mappedOrganizationId: 0,
			pccLocationName: '',
			residentCreationIsAutomatic: false,
			userCreationIsAutomatic: false,
			finalizeMeasurementAutomatically: true,
			additionalData: '',
			description: '',
		},
		pccLocations: [],
		foundMatch: false,
	}),
	computed: {
		...mapState({
			allMedicalInstitutions: (state) => state.medicalInstitutions.medicalInstitutions,
			hospitals: (state) => state.hospitals.hospitals,
		}),
		...mapGetters({
			getTimeZone: 'verifiers/getTimeZone',
			ecpLocations: 'mapping/getEcpLocation',
		}),
		detailsDrawer () {
			return this.$store.state.detailsDrawer;
		},
		viewDataState: {
			get () {
				return this.$store.state.item;
			},
			set (value) {
				this.$store.state.item = value;
			},
		},
	},
	async mounted () {
		await Promise.all([this.getMedicalInstitutions(), this.getEcpNotMappedLocation(), this.getMappedOrganizations()]);
		this.locationToAdd.pccCompanyId = this.viewDataState?.orgUuid ?? 0;
		this.locationToAdd.mappedOrganizationId = this.viewDataState?.orgId ?? 0;
		if (this.viewDataState) {
			this.pccLocations = (await this.getPCCFacilities(this.viewDataState?.orgUuid)).filter(
				(e) => e.pccLocationsMapping.length === 0,
			);
		}
		this.locationToAdd.pccLocationId = this.viewDataState?.facId ?? 0;
		this.locationToAdd.pccLocationName = this.viewDataState?.facilityName ?? '';
		this.checkLocationExists();
	},
	methods: {
		...mapActions({
			getPCCFacilityById: 'pcc/getPCCFacilityById',
			getMedicalInstitutions: 'medicalInstitutions/getMedicalInstitutions',
			getPCCFacilities: 'pcc/getPCCFacilities',
			getMappedPccLocations: 'pcc/getMappedPccLocations',
			createPCCLocationMappingAndLocation: 'pcc/createPCCLocationMappingAndLocation',
			getMappedPccOrganizations: 'pcc/getMappedPccOrganizations',
		}),
		isSaveButtonDisabled () {
			return (
				this.locationToAdd.pccLocationId === 0 ||
				this.locationToAdd.mappedHospitalId === 0 ||
				this.locationToAdd.mappedOrganizationId === 0 ||
				this.locationToAdd.pccLocationName.length === 0
			);
		},
		resetComponent () {
			this.selectedMedicalInstitution = null;
			this.ecpLocationGuid = '';
			this.ecpLocationName = '';
			this.locationToAdd = {
				ecpLocationId: '',
				mappedHospitalId: 0,
				mappedOrganizationId: 0,
				ecpLocationName: '',
				finalizeMeasurementAutomatically: true,
				residentCreationIsAutomatic: false,
				userCreationIsAutomatic: false,
			};
		},
		async saveOrganizationMapping () {
			this.createPCCLocationMappingAndLocation(this.locationToAdd).then((res) => {
				this.getMappedPccLocations();
				this.closeDrawer();
			});
		},
		closeDrawer () {
			this.resetComponent();
			this.$store.commit('SET_DETAILS_DRAWER', false);
		},
		getEcpNotMappedLocation () {
			MappingRepository.getNotMappedEcpLocation().then((res) => {
				this.notMappedLocations = res;
			});
		},
		async getMappedOrganizations () {
			const response = await this.getMappedPccOrganizations();
			if (response) {
				this.mappedPccOrganizations = response;
			}
		},
		async checkLocation () {
			const pccFacilityById = await this.getPCCFacilityById({
				pccOrgId: this.viewDataState.orgUuid,
				facilitiesById: this.viewDataState.facId,
			});
			const isLocationMapped = pccFacilityById.pccLocationsMapping.length > 0;
			if (!isLocationMapped) {
				await this.$store.commit('mapping/SELECTED_ORGANIZATION', null);
				await this.$store.commit('mapping/SELECTED_LOCATION', pccFacilityById);
				await this.$store.commit('mapping/SET_SELECTED_PCC_LOCATIONS', pccFacilityById);

				this.$store.commit('SET_EDIT_DETAILS_DRAWER', true);
			}
		},
		checkLocationExists () {
			if (!this.viewDataState) return;
			const facName = this.viewDataState.facilityName;
			const orgId = this.viewDataState.orgId;
			this.locationToAdd.mappedHospitalId = this.hospitals.find(
				(h) => h.medicalInstitutionId === orgId && h.hospitalName === facName,
			)?.id;
			if (this.locationToAdd.mappedHospitalId !== null && this.locationToAdd.mappedHospitalId !== undefined) {
				this.foundMatch = true;
			}
		},
	},
	watch: {
		'locationToAdd.mappedOrganizationId' (val) {
			this.notMappedLocationsFiltered = this.notMappedLocations.filter((n) => n.medicalInstitutionId === val);
		},
		async 'locationToAdd.pccCompanyId' (val) {
			if (!val) return;
			this.pccLocations = (await this.getPCCFacilities(val)).filter((e) => e.pccLocationsMapping.length === 0);
		},
		'locationToAdd.pccLocationId' (val) {
			this.locationToAdd.pccLocationName = this.pccLocations.find((p) => p.facId === val)?.facilityName || '';
		},
		async detailsDrawer (val) {
			if (val) {
				this.foundMatch = false;
				this.locationToAdd.pccCompanyId = this.viewDataState?.orgUuid ?? 0;
				this.locationToAdd.mappedOrganizationId = this.viewDataState?.orgId ?? 0;
				if (this.viewDataState) {
					this.pccLocations = (await this.getPCCFacilities(this.viewDataState?.orgUuid)).filter(
						(e) => e.pccLocationsMapping.length === 0,
					);
				}
				this.locationToAdd.pccLocationId = this.viewDataState?.facId ?? 0;
				this.locationToAdd.pccLocationName = this.viewDataState?.facilityName ?? '';
				this.checkLocationExists();
			}
		},
	},
};
</script>
<style scoped>
.map-container {
	margin-top: 20px;
	/* Adjust this margin as needed */
}
</style>
